import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CnetAuthenticationRequest } from '../checkout-model';
import { SessionVariable } from '../../common/enum/session-variable';
import { LocalStorageService } from '../../common/services/local-storage.service';
import { SharedSignalsService } from '../../common/services/shared-signals.service';

export interface CnetAuthContext {
  civilId: string;
  requestId: string;
}

export type CnetAuthTarget = 'newUser' | 'existing-user';

@Injectable({
  providedIn: 'root',
})
export class CnetAuthSessionService {
  private readonly router = inject(Router);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly sharedSignalsService = inject(SharedSignalsService);

  resolveCivilId(): string | null {
    return this.persistCivilId(this.readSources().civilId.value);
  }

  persistCivilId(civilId: unknown): string | null {
    const value = this.normalize(civilId);

    if (value == null) {
      return null;
    }

    this.sharedSignalsService.setNationalId(value);
    this.localStorageService.setItem(SessionVariable.NATIONAL_ID, value);

    return value;
  }

  persistRequestId(requestId: unknown): string | null {
    const value = this.normalize(requestId);

    if (value == null) {
      return null;
    }

    this.sharedSignalsService.setRequestId(value);
    this.localStorageService.setItem(SessionVariable.REQ_ID, value);

    return value;
  }

  resolve(): CnetAuthContext | null {
    const sources = this.readSources();
    const civilId = this.normalize(sources.civilId.value);
    const requestId = this.normalize(sources.requestId.value);

    if (civilId == null || requestId == null) {
      console.warn('[CINET auth] incomplete session', {
        url: this.router.url,
        civilIdPresent: civilId != null,
        requestIdPresent: requestId != null,
        civilIdSource: sources.civilId.source,
        requestIdSource: sources.requestId.source,
      });

      return null;
    }

    this.persistCivilId(civilId);
    this.persistRequestId(requestId);

    return { civilId, requestId };
  }

  toRequest(): CnetAuthenticationRequest | null {
    return this.resolve();
  }

  navigateToAuthentication(route: ActivatedRoute, target: CnetAuthTarget): boolean {
    const context = this.resolve();

    if (!context) {
      this.router.navigate(['../mobile'], { relativeTo: route });

      return false;
    }

    this.router.navigate([`../cnet-authentication-${target}`], {
      relativeTo: route,
      state: context,
    });

    return true;
  }

  returnToMobile(route: ActivatedRoute): void {
    this.router.navigate(['../mobile'], { relativeTo: route });
  }

  private readSources(): {
    civilId: { value: unknown; source: string };
    requestId: { value: unknown; source: string };
  } {
    const state = this.readNavigationState();

    return {
      civilId: this.firstPresent([
        { value: state?.civilId, source: 'navigation' },
        { value: this.sharedSignalsService.nationalId(), source: 'signal' },
        {
          value: this.localStorageService.getItem(SessionVariable.NATIONAL_ID),
          source: 'storage',
        },
        { value: this.readAuthProfileNationalId(), source: 'profile' },
      ]),
      requestId: this.firstPresent([
        { value: state?.requestId, source: 'navigation' },
        { value: this.sharedSignalsService.requestId(), source: 'signal' },
        { value: this.localStorageService.getItem(SessionVariable.REQ_ID), source: 'storage' },
      ]),
    };
  }

  private readAuthProfileNationalId(): unknown {
    const raw = this.localStorageService.getItem<unknown>(SessionVariable.AUTH_PROFILE);

    if (raw == null) {
      return null;
    }

    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw)?.nationalId;
      } catch {
        return null;
      }
    }

    if (typeof raw === 'object') {
      return (raw as { nationalId?: unknown })?.nationalId;
    }

    return null;
  }

  private readNavigationState(): Partial<CnetAuthContext> | null {
    const state = this.router.getCurrentNavigation()?.extras?.state ?? window.history.state;

    if (!state) {
      return null;
    }

    return {
      civilId: state.civilId,
      requestId: state.requestId,
    };
  }

  private firstPresent(candidates: { value: unknown; source: string }[]): {
    value: unknown;
    source: string;
  } {
    for (const candidate of candidates) {
      if (this.normalize(candidate.value) != null) {
        return candidate;
      }
    }

    return { value: null, source: 'missing' };
  }

  private normalize(value: unknown): string | null {
    if (value == null || value === '') {
      return null;
    }

    const normalized = String(value).trim();

    if (!normalized || normalized === 'null' || normalized === 'undefined') {
      return null;
    }

    return normalized;
  }
}
